import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { channels } from "@/mocks/channels";
import { articles, articlesByChannel } from "@/mocks/articles";
import { toneSoft, toneText } from "@/utils/display";

export default function ChannelsPage() {
  useSeo({
    title: "栏目总览 | 深瞳档案 六大内幕观察栏目",
    description:
      "深瞳档案栏目总览：行业内幕、商业暗流、都市奇谈、网络迷雾、历史暗影与人物观察。每个栏目对应一种观察视角，集中收录同一类逻辑的幕后内幕与隐秘事件档案。",
    keywords: "内幕栏目,揭秘栏目,行业内幕,幕后黑幕,内幕合集",
    path: "/channels",
    type: "website",
  });

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "栏目总览",
      url: "https://www.shentongdangan.cn/channels",
      inLanguage: "zh-CN",
      description: "深瞳档案全部内容栏目总览。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.shentongdangan.cn/" },
        { "@type": "ListItem", position: 2, name: "栏目总览", item: "https://www.shentongdangan.cn/channels" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "深瞳档案栏目列表",
      itemListElement: channels.map((c, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: c.name,
        url: `https://www.shentongdangan.cn/channel/${c.slug}`,
      })),
    },
  ];

  return (
    <>
      <JsonLd data={schema} />
      <div className="border-b border-background-200 bg-background-100">
        <div className="container-page">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "栏目总览" }]} />
          <div className="pb-10">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">栏目总览</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              六个固定栏目，六种观察视角。共收录 {articles.length} 篇档案，每一篇都围绕一个具体事件或现象展开，
              不做泛泛而谈的综述。
            </p>
          </div>
        </div>
      </div>

      <div className="container-page py-12">
        <div className="flex flex-col gap-12">
          {channels.map((c) => {
            const list = articlesByChannel(c.slug);
            return (
              <section key={c.slug} aria-labelledby={`ch-${c.slug}`}>
                <div className="flex flex-wrap items-end justify-between gap-4 border-b border-background-200 pb-4">
                  <div className="flex items-start gap-4">
                    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md ${toneSoft[c.tone]}`}>
                      <i className={`${c.icon} text-xl`} aria-hidden="true" />
                    </span>
                    <div>
                      <h2 id={`ch-${c.slug}`} className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                        {c.name}
                      </h2>
                      <p className={`mt-1 text-xs font-medium ${toneText[c.tone]}`}>
                        {c.tagline} · 共 {list.length} 篇
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/channel/${c.slug}`}
                    className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
                  >
                    进入栏目
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600">{c.description}</p>
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((a) => (
                    <ArticleCard key={a.slug} article={a} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </>
  );
}